import React, { useState } from 'react';
import { Lock } from 'lucide-react';
import { KLEUR, Tooltip, euro, maandLabel, niceMax, useBreedte, type StapelReeks } from '../data/DataCharts';

/**
 * Aanvullende SVG-grafieken voor Data (Menu 4) van de professional, in dezelfde
 * stijl als components/data/DataCharts: echte pixelbreedte, één y-as,
 * hover-tooltip per mark, 2px oppervlak-spleet tussen fills.
 * `null` betekent overal: onderdrukt door een privacy-/ACM-grendel.
 */

const MARKER = '#0B1F3A';

// ─── 5.1 / 5.5 Horizontale staven (optioneel met verticale benchmarklijn) ────

export interface BalkRij {
  key: string;
  label: string;
  waarde: number | null;
  sub?: string;
}

export const HorizontaleBalken: React.FC<{
  rijen: BalkRij[];
  kleur?: string;
  max?: number;
  formaat?: (v: number) => string;
  referentie?: { waarde: number; label: string } | null;
  onderdruktLabel?: string;
}> = ({ rijen, kleur = KLEUR.blauw, max, formaat = (v) => String(v), referentie, onderdruktLabel }) => {
  const [hover, setHover] = useState<string | null>(null);
  const top = max ?? niceMax(Math.max(1, ...rijen.map((r) => r.waarde ?? 0), referentie?.waarde ?? 0));
  const p = (v: number) => `${Math.min(100, Math.max(0, (v / top) * 100))}%`;
  return (
    <div className="space-y-2.5">
      {rijen.map((r) => (
        <div key={r.key} className="grid grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-3 text-sm sm:grid-cols-[minmax(0,18rem)_1fr_auto]" onMouseEnter={() => setHover(r.key)} onMouseLeave={() => setHover(null)}>
          <span className="truncate text-gray-700" title={r.label}>{r.label}</span>
          <div className="relative h-6">
            {r.waarde == null ? (
              <div className="flex h-full items-center gap-1.5 rounded border border-dashed border-amber-300 bg-amber-50 px-2 text-xs text-amber-800">
                <Lock className="h-3 w-3 flex-shrink-0" aria-hidden />
                <span className="truncate">{onderdruktLabel}</span>
              </div>
            ) : (
              <div className="h-full rounded-r" style={{ width: p(r.waarde), background: kleur, opacity: hover === null || hover === r.key ? 1 : 0.55, minWidth: r.waarde > 0 ? 4 : 0 }} />
            )}
            {referentie && <div className="pointer-events-none absolute -bottom-1 -top-1 border-l-2 border-dashed" style={{ left: p(referentie.waarde), borderColor: MARKER }} />}
          </div>
          <span className="w-16 text-right font-semibold text-gray-900">{r.waarde == null ? '—' : formaat(r.waarde)}</span>
          {r.sub && hover === r.key && <span className="col-span-3 -mt-1.5 text-xs text-gray-500 sm:col-start-2">{r.sub}</span>}
        </div>
      ))}
      {referentie && (
        <p className="flex items-center gap-2 pt-1 text-xs text-gray-600">
          <span className="inline-block h-3 border-l-2 border-dashed" style={{ borderColor: MARKER }} />
          {referentie.label}: <strong className="text-gray-900">{formaat(referentie.waarde)}</strong>
        </p>
      )}
    </div>
  );
};

// ─── Kolommen per categorie: gegroepeerd (5.4) of enkel met lijn (5.7) ───────

export interface KolomReeks {
  key: string;
  label: string;
  kleur: string;
}

export const KolomGrafiek: React.FC<{
  categorieen: { key: string; label: string; waarden: Record<string, number | null>; sub?: string }[];
  reeksen: KolomReeks[];
  formaat?: (v: number) => string;
  referentie?: { waarde: number; label: string } | null;
  onderdruktLabel?: string;
  hoogte?: number;
}> = ({ categorieen, reeksen, formaat = (v) => String(v), referentie, onderdruktLabel = '—', hoogte = 260 }) => {
  const { ref, w: W } = useBreedte();
  const [hover, setHover] = useState<number | null>(null);
  const H = hoogte;
  const padL = 56;
  const padR = 12;
  const padT = 16;
  const padB = 34;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const n = Math.max(1, categorieen.length);
  const kolom = plotW / n;
  const alle = categorieen.flatMap((c) => reeksen.map((r) => c.waarden[r.key] ?? 0));
  const yMax = niceMax(Math.max(1, ...alle, referentie?.waarde ?? 0) * 1.1);
  const y = (v: number) => padT + plotH * (1 - v / yMax);
  const groepB = Math.min(kolom * 0.7, 36 * reeksen.length + 2 * (reeksen.length - 1));
  const staafB = (groepB - 2 * (reeksen.length - 1)) / reeksen.length;
  const maxTekens = Math.max(6, Math.floor(kolom / 6.5));

  return (
    <div className="relative" ref={ref}>
      <svg width={W} height={H} role="img" onMouseLeave={() => setHover(null)}>
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={y(yMax * t)} y2={y(yMax * t)} stroke={KLEUR.grid} />
            <text x={padL - 6} y={y(yMax * t) + 4} textAnchor="end" fontSize={11} fill={KLEUR.as}>{formaat(yMax * t)}</text>
          </g>
        ))}
        {categorieen.map((c, i) => {
          const x0 = padL + kolom * i + (kolom - groepB) / 2;
          return (
            <g key={c.key} opacity={hover === null || hover === i ? 1 : 0.55}>
              {reeksen.map((r, j) => {
                const v = c.waarden[r.key];
                const x = x0 + j * (staafB + 2);
                if (v == null) {
                  return (
                    <g key={r.key}>
                      <rect x={x} y={padT + plotH - 40} width={staafB} height={40} rx={4} fill="none" stroke="#d97706" strokeDasharray="4 3" />
                      {/* slotje: beugel + body */}
                      <path d={`M${x + staafB / 2 - 4},${padT + plotH - 22} v-3 a4,4 0 0 1 8,0 v3`} fill="none" stroke="#b45309" strokeWidth={1.5} />
                      <rect x={x + staafB / 2 - 6} y={padT + plotH - 22} width={12} height={9} rx={2} fill="#b45309" />
                    </g>
                  );
                }
                if (v <= 0) return null;
                const h = Math.max(1, padT + plotH - y(v));
                return <path key={r.key} d={`M${x},${padT + plotH} v${-(h - 4)} q0,-4 4,-4 h${Math.max(0, staafB - 8)} q4,0 4,4 v${h - 4} z`} fill={r.kleur} />;
              })}
            </g>
          );
        })}
        {referentie && (
          <g>
            <line x1={padL} x2={W - padR} y1={y(referentie.waarde)} y2={y(referentie.waarde)} stroke={MARKER} strokeWidth={2} strokeDasharray="6 4" />
          </g>
        )}
        {categorieen.map((c, i) => (
          <text key={`l-${c.key}`} x={padL + kolom * i + kolom / 2} y={H - 14} textAnchor="middle" fontSize={11} fill={KLEUR.as}>
            {c.label.length > maxTekens ? `${c.label.slice(0, maxTekens - 1)}…` : c.label}
          </text>
        ))}
        {categorieen.map((c, i) => (
          <rect key={`hit-${c.key}`} x={padL + kolom * i} y={padT} width={kolom} height={plotH} fill="transparent" onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} tabIndex={0} />
        ))}
      </svg>
      {hover !== null && (
        <Tooltip x={padL + kolom * hover + kolom / 2} w={W}>
          <p className="font-semibold text-gray-900">{categorieen[hover].label}</p>
          {reeksen.map((r) => {
            const v = categorieen[hover].waarden[r.key];
            return (
              <p key={r.key} className="flex items-center justify-between gap-2 text-gray-700">
                <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-sm" style={{ background: r.kleur }} />{r.label}</span>
                <strong>{v == null ? onderdruktLabel : formaat(v)}</strong>
              </p>
            );
          })}
          {categorieen[hover].sub && <p className="mt-1 text-gray-500">{categorieen[hover].sub}</p>}
          {referentie && <p className="mt-1 border-t border-gray-100 pt-1 text-gray-700">{referentie.label}: <strong>{formaat(referentie.waarde)}</strong></p>}
        </Tooltip>
      )}
    </div>
  );
};

// ─── 5.2 Gestapelde maandkolommen + dunne lijn vorig jaar (zelfde €-as) ──────

export const StapelMetVorigJaar: React.FC<{
  maanden: string[];
  reeksen: StapelReeks[];
  waarden: Record<string, number>[];
  vorigJaar: number[] | null;
  vorigJaarLabel: string;
  hoogte?: number;
}> = ({ maanden, reeksen, waarden, vorigJaar, vorigJaarLabel, hoogte = 280 }) => {
  const { ref, w: W } = useBreedte();
  const [hover, setHover] = useState<number | null>(null);
  const H = hoogte;
  const padL = 64;
  const padR = 12;
  const padT = 12;
  const padB = 28;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const n = Math.max(1, maanden.length);
  const kolom = plotW / n;
  const totalen = waarden.map((w) => reeksen.reduce((s, r) => s + (w[r.key] || 0), 0));
  const yMax = niceMax(Math.max(1, ...totalen, ...(vorigJaar ?? [0])) * 1.1);
  const schaal = (v: number) => (plotH * v) / yMax;
  const staafB = Math.max(6, Math.min(40, kolom * 0.6));
  const xMid = (i: number) => padL + kolom * i + kolom / 2;
  const labelElke = Math.ceil(n / Math.max(1, Math.floor(plotW / 48)));

  return (
    <div className="relative" ref={ref}>
      <svg width={W} height={H} role="img" onMouseLeave={() => setHover(null)}>
        {[0, 0.25, 0.5, 0.75, 1].map((t) => (
          <g key={t}>
            <line x1={padL} x2={W - padR} y1={padT + plotH * (1 - t)} y2={padT + plotH * (1 - t)} stroke={KLEUR.grid} />
            <text x={padL - 6} y={padT + plotH * (1 - t) + 4} textAnchor="end" fontSize={11} fill={KLEUR.as}>{euro(yMax * t)}</text>
          </g>
        ))}
        {waarden.map((w, i) => {
          let yy = padT + plotH;
          const x = xMid(i) - staafB / 2;
          const zichtbaar = reeksen.filter((r) => (w[r.key] || 0) > 0);
          return (
            <g key={maanden[i]} opacity={hover === null || hover === i ? 1 : 0.55}>
              {zichtbaar.map((r, j) => {
                const h = schaal(w[r.key]);
                yy -= h;
                const boven = j === zichtbaar.length - 1;
                const hNetto = Math.max(1, h - (boven ? 0 : 2));
                return boven ? (
                  <path key={r.key} d={`M${x},${yy + hNetto} v${-Math.max(0, hNetto - 4)} q0,-4 4,-4 h${staafB - 8} q4,0 4,4 v${Math.max(0, hNetto - 4)} z`} fill={r.kleur} />
                ) : (
                  <rect key={r.key} x={x} y={yy + 2} width={staafB} height={hNetto} fill={r.kleur} />
                );
              })}
            </g>
          );
        })}
        {vorigJaar && (
          <>
            <polyline points={vorigJaar.map((v, i) => `${xMid(i)},${padT + plotH - schaal(v)}`).join(' ')} fill="none" stroke={MARKER} strokeWidth={1.5} strokeDasharray="5 3" strokeLinejoin="round" />
            {vorigJaar.map((v, i) => (
              <circle key={i} cx={xMid(i)} cy={padT + plotH - schaal(v)} r={hover === i ? 4.5 : 3} fill="#fff" stroke={MARKER} strokeWidth={1.5} />
            ))}
          </>
        )}
        {maanden.map((m, i) =>
          i % labelElke === 0 ? (
            <text key={m} x={xMid(i)} y={H - 8} textAnchor="middle" fontSize={11} fill={KLEUR.as}>{maandLabel(m)}</text>
          ) : null
        )}
        {maanden.map((m, i) => (
          <rect key={`hit-${m}`} x={padL + kolom * i} y={padT} width={kolom} height={plotH} fill="transparent" onMouseEnter={() => setHover(i)} onFocus={() => setHover(i)} tabIndex={0} />
        ))}
      </svg>
      {hover !== null && (
        <Tooltip x={xMid(hover)} w={W}>
          <p className="font-semibold text-gray-900">{maandLabel(maanden[hover])}</p>
          {reeksen.map((r) => (
            <p key={r.key} className="flex items-center justify-between gap-2 text-gray-700">
              <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-sm" style={{ background: r.kleur }} />{r.label}</span>
              <strong>{euro(waarden[hover][r.key] || 0)}</strong>
            </p>
          ))}
          <p className="mt-1 border-t border-gray-100 pt-1 text-gray-900">Totaal <strong>{euro(totalen[hover])}</strong></p>
          {vorigJaar && <p className="text-gray-600">{vorigJaarLabel}: <strong>{euro(vorigJaar[hover])}</strong></p>}
        </Tooltip>
      )}
    </div>
  );
};

// ─── 5.6 Radar (eigen score vs landelijk, schaal 1–5) ────────────────────────

export const RadarGrafiek: React.FC<{
  assen: { key: string; label: string }[];
  reeksen: { key: string; label: string; kleur: string; waarden: Record<string, number | null>; gestippeld?: boolean }[];
  max?: number;
}> = ({ assen, reeksen, max = 5 }) => {
  const [hover, setHover] = useState<number | null>(null);
  const C = 160;
  const R = 105;
  const n = assen.length;
  const hoek = (i: number) => -Math.PI / 2 + (i * 2 * Math.PI) / n;
  const punt = (i: number, v: number) => [C + R * (v / max) * Math.cos(hoek(i)), C + R * (v / max) * Math.sin(hoek(i))];
  return (
    <svg viewBox="-50 0 420 320" className="mx-auto h-auto w-full max-w-md" role="img" onMouseLeave={() => setHover(null)}>
      {[1, 2, 3, 4, 5].map((s) => (
        <polygon key={s} points={assen.map((_, i) => punt(i, s).join(',')).join(' ')} fill="none" stroke={KLEUR.grid} />
      ))}
      {assen.map((a, i) => {
        const [x, y] = punt(i, max);
        const [lx, ly] = punt(i, max * 1.2);
        const cos = Math.cos(hoek(i));
        return (
          <g key={a.key}>
            <line x1={C} y1={C} x2={x} y2={y} stroke={KLEUR.grid} />
            <text x={lx} y={ly + 4} textAnchor={Math.abs(cos) < 0.2 ? 'middle' : cos > 0 ? 'start' : 'end'} fontSize={11} fill={hover === i ? '#111827' : KLEUR.as} fontWeight={hover === i ? 600 : 400}>
              {a.label}
            </text>
          </g>
        );
      })}
      {reeksen.map((r) => {
        const pts = assen.map((a, i) => punt(i, r.waarden[a.key] ?? 0).join(',')).join(' ');
        return (
          <g key={r.key}>
            <polygon points={pts} fill={r.gestippeld ? 'none' : r.kleur} fillOpacity={0.15} stroke={r.kleur} strokeWidth={2} strokeDasharray={r.gestippeld ? '5 3' : undefined} strokeLinejoin="round" />
            {assen.map((a, i) => {
              const v = r.waarden[a.key];
              if (v == null) return null;
              const [x, y] = punt(i, v);
              return <circle key={a.key} cx={x} cy={y} r={hover === i ? 5 : 3.5} fill="#fff" stroke={r.kleur} strokeWidth={2} />;
            })}
          </g>
        );
      })}
      {assen.map((a, i) => {
        const [x, y] = punt(i, max);
        return (
          <circle key={`hit-${a.key}`} cx={(x + C) / 2} cy={(y + C) / 2} r={42} fill="transparent" onMouseEnter={() => setHover(i)} tabIndex={0} onFocus={() => setHover(i)}>
            <title>{`${a.label}: ${reeksen.map((r) => `${r.label} ${r.waarden[a.key]?.toLocaleString('nl-NL') ?? '—'}`).join(' · ')}`}</title>
          </circle>
        );
      })}
    </svg>
  );
};
